import { isCustomFont, loadCustomFont, resolveFontFamily } from '@repo/ui';
import { MuiAccordion } from './MuiAccordion';
import { MuiAccordionDetails } from './MuiAccordionDetails';
import { MuiAccordionSummary } from './MuiAccordionSummary';
import { MuiAlert } from './MuiAlert';
import { MuiButton } from './MuiButton';
import { MuiCheckbox } from './MuiCheckbox';
import { MuiChip } from './MuiChip';
import { MuiDivider } from './MuiDivider';
import { MuiFormControlLabel } from './MuiFormControlLabel';
import { MuiFormLabel } from './MuiFormLabel';
import { MuiInputBase } from './MuiInputBase';
import { MuiLink } from './MuiLink';
import { MuiList } from './MuiList';
import { MuiModal } from './MuiModal';
import { MuiPaginationItem } from './MuiPaginationItem';
import { MuiPaper } from './MuiPaper';
import { MuiRadio } from './MuiRadio';
import { MuiSelect } from './MuiSelect';
import { MuiTab } from './MuiTab';
import { MuiTabs } from './MuiTabs';
import { MuiTextField } from './MuiTextField';
import { MuiTooltip } from './MuiTooltip';
import { MuiTypography } from './MuiTypography';
import { breakpoints } from './breakpoints';
import { MuiAvatar, MuiIconButton, MuiAutocomplete } from './components';
import { palette } from './palette';
import { typography } from './typography';
import { typeface } from 'settings/tokens';

import { PaletteOptions, ThemeOptions } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { Theme } from 'settings/mui/palette';

/**
 * Weights each family ACTUALLY ships on Google Fonts.
 *
 * This is not pedantry. Lato has no 500 and no 600, but the app asks for
 * `fontWeight: 500` at 55 sites and `600` at 32. The CSS font-matching
 * algorithm resolves a missing 500 DOWNWARD (to 400) and a missing 600 UPWARD
 * (to 700), so every "medium" in this app renders identical to regular and
 * every "semibold" renders as full bold. Together with a type scale that
 * collapsed nine variants into four sizes, that left 4 sizes x 2 real weights
 * to express the whole hierarchy — which is why it read flat.
 *
 * Requesting only the real weights does not fix the snapping (the browser does
 * that), but it stops us pretending a family has a range it does not, and it
 * makes the constraint visible at the one place fonts are chosen.
 */
const FONT_WEIGHTS: Record<string, string> = {
  Lato: '300;400;700',
  Inter: '300;400;500;600;700',
  'Space Mono': '400;700',
};

/** Families with a genuine 100-900 range get the full request. */
const DEFAULT_WEIGHTS = '300;400;500;600;700';

const loadGoogleFont = (fontName: string) => {
  // An UPLOADED font is a URL, not a family, so it takes the @font-face path instead of a
  // Google stylesheet request. Delegated to @repo/ui rather than reimplemented: this file
  // and packages/ui both load fonts, and a custom font that worked in only one of them
  // would render in the platform's Studio and not on the certificate it is previewing —
  // which is the surface that matters.
  if (isCustomFont(fontName)) {
    loadCustomFont(fontName);
    return;
  }
  // A font STACK ("-apple-system, …") is not a Google family and must not be
  // requested — doing so 404s and, worse, reads as a deliberate choice later.
  if (!fontName || /[,'"]/.test(fontName) || fontName.startsWith('-')) return;
  const fontFamily = fontName.replace(/\s+/g, '+');
  const existingLink = document.querySelector(
    `link[href*="fonts.googleapis.com"][href*="${fontFamily}"]`
  );

  if (existingLink) {
    return;
  }

  const link = document.createElement('link');
  const weights = FONT_WEIGHTS[fontName] ?? DEFAULT_WEIGHTS;
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily}:wght@${weights}&display=swap`;
  link.rel = 'stylesheet';
  document.head.appendChild(link);
};

/**
 * Dev-only font override, e.g. `?font=Space+Mono` or `?font=Inter`.
 *
 * Exists so a typeface can be COMPARED on the running app in seconds rather
 * than by editing a constant and reloading. A brand's Studio font still wins;
 * this only replaces the Vera default, and only when the query param is present.
 */
const VALID_FONT = /^[\w \-]{2,40}$/;

/**
 * Read from `localStorage`, NOT the URL.
 *
 * A `?font=` query param cannot work here: the app strips its own query string
 * during bootstrap (it runs on a MemoryRouter), so the param is gone before the
 * theme settles — measured, the first render honoured it and every render after
 * fell back to Lato. `localStorage` survives the rewrite and the reload.
 *
 *     localStorage.setItem('vera:font', 'Space Mono'); location.reload()
 *     localStorage.removeItem('vera:font');            location.reload()
 */
const readDevFont = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const f = window.localStorage?.getItem('vera:font');
    return f && VALID_FONT.test(f) ? f : null;
  } catch {
    return null;
  }
};

const devFontOverride = (): string | null => readDevFont();

export const creteMuiTheme = (companyTheme: Theme | null, shadowRoot?: ShadowRoot | null) => {
  // The dev override wins over the tenant's Studio font deliberately: the seeded
  // tenant SETS `font: 'Lato'`, so an override placed after it would never apply
  // and the switcher would silently do nothing. It is gated behind an explicit
  // localStorage key no real user sets.
  const devFont = devFontOverride();
  const font = devFont || companyTheme?.font || typeface.sans;
  const titleFont = devFont || companyTheme?.title_font || font;
  // `font` may be a STACK (the system-first default), which cannot be fetched.
  // Load only single family names; the stack's webfont is requested separately.
  loadGoogleFont(font);
  loadGoogleFont(titleFont);
  loadGoogleFont(typeface.webfont);

  const baseRadius = 8;
  // Button corner radius from the company theme. Use a finite-check (not `||`)
  // so a deliberate 0 ("Sharp") isn't replaced by the default (VERA-270).
  const themeRadius = Number(companyTheme?.border_radius);
  const buttonRadius = Number.isFinite(themeRadius) ? themeRadius : baseRadius;

  // Create a container inside the shadow DOM for portals
  let portalContainer: HTMLElement | undefined = undefined;

  if (shadowRoot) {
    // Find the #vera-shadow-root element instead of using shadowRoot directly
    const veraRoot = shadowRoot.getElementById('vera-shadow-root');

    if (veraRoot) {
      // Create or reuse a container for portals INSIDE #vera-shadow-root
      let container = veraRoot.querySelector('#mui-portal-container') as HTMLElement;
      if (!container) {
        container = document.createElement('div');
        container.id = 'mui-portal-container';
        veraRoot.appendChild(container);
      }
      portalContainer = container as HTMLElement;
    }
  }

  const themePalette = palette(companyTheme) as PaletteOptions;

  const themeConfig: ThemeOptions = {
    breakpoints,
    palette: themePalette,
    direction: 'ltr',
    typography: typography(font, titleFont),
    shape: {
      // VERA-837: the company theme's radius is the SINGLE source for corner
      // rounding, so fields, buttons and surfaces all match. This was
      // `baseRadius` (a hardcoded 8) while buttons used `buttonRadius`, so a
      // brand's chosen radius applied to buttons but not to inputs.
      borderRadius: buttonRadius,
    },
    components: {
      MuiModal: {
        ...MuiModal,
        defaultProps: {
          ...(MuiModal?.defaultProps || {}),
          container: portalContainer,
        },
      },
      MuiPopover: {
        defaultProps: {
          container: portalContainer,
        },
      },
      MuiPopper: {
        defaultProps: {
          container: portalContainer,
        },
      },
      MuiDialog: {
        defaultProps: {
          container: portalContainer,
        },
      },
      MuiDrawer: {
        defaultProps: {
          container: portalContainer,
        },
        styleOverrides: {
          paper: {
            '@media (min-width: 600px)': {
              borderRadius: '4px',
            },
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          container: portalContainer,
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          'svg, svg *': {
            strokeWidth: '1.25 !important',
          },
          body: {
            fontFamily: resolveFontFamily(font) ?? font,
          },
          'p, div, span, a, h1, h2, h3, h4, h5, h6': {
            fontFamily: resolveFontFamily(font) ?? font,
          },
        },
      },
      MuiUseMediaQuery: {
        defaultProps: {
          noSsr: true,
        },
      },
      MuiTab: {
        ...MuiTab,
        defaultProps: {
          style: {
            borderRadius: buttonRadius,
            margin: '0 6px 4px',
          },
        },
      },
      MuiTabs,
      MuiChip,
      MuiList,
      MuiLink,
      MuiRadio,
      MuiPaper,
      MuiAlert,
      MuiButton: {
        ...MuiButton,
        defaultProps: {
          style: {
            borderRadius: buttonRadius,
          },
        },
      },
      MuiSelect,
      MuiDivider,
      MuiTooltip,
      MuiCheckbox,
      MuiFormLabel,
      MuiInputBase,
      MuiTextField,
      MuiAccordion,
      MuiTypography,
      MuiPaginationItem,
      MuiFormControlLabel,
      MuiAccordionSummary,
      MuiAccordionDetails,
      MuiAvatar,
      MuiIconButton,
      MuiAutocomplete,
    },
  };

  // First pass: Create base theme with custom palette
  const baseTheme = createTheme(themeConfig);

  // Second pass: Ensure custom colors are preserved by merging palette again
  // This is necessary because MUI's createTheme may not preserve all custom palette colors
  const finalTheme = createTheme(baseTheme, {
    palette: themePalette,
  });

  return finalTheme;
};
